<script setup lang="ts">
import { Bar } from 'vue-chartjs'
import { sendGraphQLQuery } from '@/services/gql-util'
import type { ChartData } from 'chart.js'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ref, onMounted, Ref,inject,onBeforeUnmount,nextTick,computed } from 'vue'
import { useResizeObserver } from '@vueuse/core'

// Define interface for the data items
interface StorageDataItem {
  day: string
  current: number
  deleted: number
  cumulative: number
}

// Combine an Option type with only required components and charts

const currentStorageLabel = ref(0)
const deletedBytes = ref(0)
const itemsDeleted = ref(0)
const loading = ref(true)
const dates = ref<string[]>([])
const newStorageData = ref<number[]>([])
const deletedStorageData = ref<number[]>([])
const cumulativeData = ref<number[]>([])

const vhostId = inject<Ref<string> | null>('vhostId', null);
const chartData = ref<ChartData<'bar'>>({
	        labels: [],
	        datasets: []
})
function getCssVar(name: string): string {
	        const style = getComputedStyle(document.documentElement)
	        return style.getPropertyValue(name)?.trim() || ''
}

function hslVar(name: string, fallback: string = '#3b82f6'): string {
	        const value = getCssVar(name)
	        return value && /^\d+(\s+\d+%){2}$/.test(value) ? `hsl(${value})` : fallback
}
const props = defineProps<{
			                        size?:  'small' | 'medium' | 'large',
			                        height?: number
}>()
const fontSizes = {
	        small: 8,
	        medium: 10, 
	        large: 12
}

const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}
const chartOptions = ref({
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			display: false
		}
	},
	scales: {
		y: {
			beginAtZero: true,
			ticks: { 
				font: {
					size: fontSizes[props.size || 'large']
				}
			}
		},
		x: {
			ticks: {
				font: {
					size: fontSizes[props.size || 'large']
				}
			}
		}
	}
})


interface StorageRecord {
  day: string
  daily_sum: string
  items_deleted: string
  bytes_deleted: string
  cumulative_sum: string
}

const fetchCurrentStorage = async () => {
	try {

		if(vhostId) {
			const query = `
				query ($vhost: Int!, $days: Int) {
					Vhost {
						getCurrentStorage(vhost: $vhost, days: $days) {
							day
							daily_sum
							items_deleted
							bytes_deleted
							cumulative_sum
						}
					}
				}
			`
			const data = await sendGraphQLQuery<{ Vhost: { getCurrentStorage: StorageRecord[] } }>(query, {
				vhost: Number(vhostId.value),
				days: 7,
			})

			const result = data?.Vhost?.getCurrentStorage || []
			const processedData: StorageDataItem[] = result.map((item: any) => ({
				day: item.day,
				current: Number(item.daily_sum || 0),
				deleted: Number(item.bytes_deleted || 0),
				cumulative: Number(item.cumulative_sum || 0)
			}))


			chartData.value = {
				labels: processedData.map(u => u.day),
				datasets: [{
					label: 'Uploads',
					data: processedData.map(u => u.current),
					backgroundColor:  hslVar('--primary'),
					borderColor:  hslVar('--ring'),
					hoverBackgroundColor:  hslVar('--primary-foreground'),
					hoverBorderColor:  hslVar('--primary'),
					borderWidth: 1,

				}]
			};


			if (processedData.length > 0) {
				const last = processedData[processedData.length - 1]
				currentStorageLabel.value = last.cumulative
				deletedBytes.value = last.deleted
				itemsDeleted.value = Number(result[result.length - 1].items_deleted) || 0
			}

			const dates = processedData.map((d: StorageDataItem) => d.day)
			const newStorageData = processedData.map((d: StorageDataItem) => d.current)
			const deletedStorageData = processedData.map((d: StorageDataItem) => d.deleted)
			const cumulativeData = processedData.map((d: StorageDataItem) => d.cumulative)

			const dailyMax = Math.max(...processedData.map((d: StorageDataItem) => d.current + d.deleted))
			const cumulativeMax = Math.max(...processedData.map((d: StorageDataItem) => d.cumulative))
/*
			const series: (BarSeriesOption | LineSeriesOption)[] = [
				{
					name: 'New Storage',
					type: 'bar',
					data: newStorageData,
					itemStyle: {
						color: '#1E88E5'
					},
					emphasis: {
						focus: 'series'
					},
					barMaxWidth: 40
				},
				{
					name: 'Deleted Storage',
					type: 'bar',
					data: deletedStorageData,
					itemStyle: {
						color: '#E53935'
					},
					emphasis: {
						focus: 'series'
					},
					barMaxWidth: 40
				},
				{
					name: 'Cumulative Sum',
					type: 'line',
					data: cumulativeData,
					yAxisIndex: 1,
					symbol: 'circle',
					symbolSize: 8,
					itemStyle: {
						color: '#00ACC1'
					},
					lineStyle: {
						width: 3
					},
					label: {
						show: true,
						formatter: (params: any) => formatBytes(params.value as number),
						position: 'top',
						color: '#00ACC1',
						backgroundColor: 'white',
						padding: [4, 6],
						borderRadius: 4,
						borderWidth: 1,
						borderColor: '#00ACC1'
					}
				}
			]*/
			/*
			const option: ECOption = {
				tooltip: {
					trigger: 'axis',
					axisPointer: {
						type: 'shadow'
					},
					formatter: (params: any) => {
						if (!Array.isArray(params)) return ''
						let result = `${params[0].axisValue}<br>`
						params.forEach((param: any) => {
							const seriesName = param.seriesName
							const value = param.value
							const color = param.color
							result += `
	      <span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${color}"></span>
	      ${seriesName}: <strong>${formatBytes(value)}</strong><br>
	    `
						})
						return result
					}
				},
				legend: {
					data: ['New Storage', 'Deleted Storage', 'Cumulative Sum'],
					top: 0
				},
				grid: {
					left: '3%',
					right: '4%',
					bottom: '3%',
					top: '15%',
					containLabel: true
				},
				xAxis: {
					type: 'category',
					data: dates,
					axisLabel: {
						formatter: (value: string) => new Date(value).toLocaleDateString()
					}
				},
				yAxis: [
					{
						type: 'value',
						name: 'Daily Storage',
						nameTextStyle: {
							color: '#008FFB'
						},
						axisLabel: {
							color: '#008FFB',
							formatter: formatBytes
						},
						max: Math.ceil(dailyMax * 1.1)
					},
					{
						type: 'value',
						name: 'Cumulative Sum',
						nameTextStyle: {
							color: '#00E396'
						},
						axisLabel: {
							color: '#00E396',
							formatter: formatBytes
						},
						max: Math.ceil(cumulativeMax * 1.1),
						splitLine: {
							show: false
						}
					}
				],
				series: series,
				dataZoom: [
					{
						type: 'inside',
						start: 0,
						end: 100
					},
					{
						start: 0,
						end: 100
					}
				]
			}*/

		}
	} catch (error) {
		console.error('Error fetching storage data:', error)
	} finally {
		loading.value = false
	}
}

// Handle resize

onMounted(() => {
  fetchCurrentStorage()
})
</script>

<template>
	<Card :style="'height:280px'" v-if="currentStorageLabel > 0">
	<CardHeader class="p-2">
	<CardTitle class="text-center text-subtitle">CURRENT STORAGE</CardTitle>
	</CardHeader>
	<CardContent style="height:200px" class="p-2">
	<div class="text-center mb-2">
		<span class="text-headline font-heavy">{{ formatBytes(currentStorageLabel) }}</span>
	</div>
	<div style="height:160px">
	<Bar :data="chartData" :options="chartOptions"/>
	</div>

	</CardContent>
	</Card>
</template>
